import React, { useState } from 'react';
import { Box, Text, Spinner, Center, VStack, HStack, Button, Heading, Select, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';
import { Team } from '../store/myLeagueStore';
import { useShoppingCartStore } from '../store/shoppingCartStore';
import GlassCard from './GlassCard';
import { useUserContext } from '../contexts/UserContext';
import useShopInventory, { StoreItem } from '../hooks/useShopInventory';
import ConfirmDialog from './ConfirmDialog';
import { useMyLeagueStore } from '../store/myLeagueStore';

interface UpgradeShopProps {
  rosterLoading: boolean;
  totalPayroll: number;
  team: Team | null;
}

const RENEW_FEE = {
  "FREE": 1.49,
  "BRONZE": 6.99,
  "SILVER": 13.99,
  "GOLD": 29.99,
}

const UpgradeShop: React.FC<UpgradeShopProps> = ({
  rosterLoading,
  totalPayroll,
  team,
}) => {

  const { addItem } = useShoppingCartStore();
  const { currentLeague, myTeam } = useUserContext();
  const { myLeague } = useMyLeagueStore();
  const { items: inventoryItems, salaryCapItems, loading: inventoryLoading, error: inventoryError, refetch } = useShopInventory({
    leagueId: currentLeague?.league_id,
    teamId: myTeam ?? undefined,
    activeOnly: true,
  });
  const [selectedSalaryCapItem, setSelectedSalaryCapItem] = useState<string>('');
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [pendingItem, setPendingItem] = useState<StoreItem | null>(null);
  const renewFee = RENEW_FEE[myLeague?.tier as keyof typeof RENEW_FEE] || 1.49;
  const { hasSalaryCapItem, getSalaryCapItem, removeItem } = useShoppingCartStore();

  // Get current salary cap level from team
  const currentSalaryCapLevel = (team as any)?.salary_cap_level || 0;

  // Helper function to extract level from a salary cap item
  const getItemLevel = (item: StoreItem): number | null => {
    const raw = item.raw as any;
    // Try various possible locations for the level
    const level = raw?.item?.metadata?.level;
    return level !== null && level !== undefined ? Number(level) : null;
  };

  // Helper function to extract salary cap increase amount from item (in millions)
  const getItemIncrease = (item: StoreItem): number => {
    const raw = item.raw as any;
    // Try various possible locations for the increase amount
    const increase = raw?.item?.metadata?.salary_cap_increase ?? 
                    raw?.item?.metadata?.salaryCapIncrease ??
                    raw?.metadata?.salary_cap_increase ??
                    raw?.metadata?.salaryCapIncrease ??
                    null;
    // Increase is already in millions
    return increase !== null && increase !== undefined ? Number(increase) : 0;
  };
  console.log('salaryCapItems', salaryCapItems);

  // Filter salary cap items to only show levels above current level
  const availableSalaryCapItems = salaryCapItems
    .filter((item) => {
      const itemLevel = getItemLevel(item);
      // If we can't determine the level, include it (to be safe)
      if (itemLevel === null) return true;
      // Only show items with level greater than current level
      return itemLevel > currentSalaryCapLevel;
    })
    .sort((a, b) => {
      // Sort by level ascending
      const levelA = getItemLevel(a) ?? 0;
      const levelB = getItemLevel(b) ?? 0;
      return levelA - levelB;
    });

  // Get items included in cumulative price calculation
  const getIncludedItems = (selectedItemId: string): StoreItem[] => {
    if (!selectedItemId) return [];
    
    const selectedItem = availableSalaryCapItems.find((i) => i.id === selectedItemId);
    if (!selectedItem) return [];
    
    const selectedLevel = getItemLevel(selectedItem);
    if (selectedLevel === null) return [selectedItem];
    
    // Get all items from first available level up to and including selected level
    return availableSalaryCapItems.filter((item) => {
      const itemLevel = getItemLevel(item);
      return itemLevel !== null && itemLevel <= selectedLevel;
    });
  };

  // Calculate cumulative price for selected item (selected + all previous items)
  const getCumulativePrice = (selectedItemId: string): number => {
    const includedItems = getIncludedItems(selectedItemId);
    return includedItems.reduce((sum, item) => sum + item.price, 0);
  };

  // Calculate cumulative increase for an item (sum of all increases up to and including this item)
  const getCumulativeIncrease = (itemId: string): number => {
    const includedItems = getIncludedItems(itemId);
    return includedItems.reduce((sum, item) => sum + getItemIncrease(item), 0);
  };

  // Generate price breakdown as bullet points
  const getPriceBreakdown = (selectedItemId: string): React.ReactNode => {
    if (!selectedItemId) return null;
    
    const includedItems = getIncludedItems(selectedItemId);
    if (includedItems.length === 0) return null;
    
    if (includedItems.length === 1) {
      return (
        <Text fontSize="xs" color="gray.400" fontStyle="italic">
          Price: ${includedItems[0].price.toFixed(2)}
        </Text>
      );
    }
    
    return (
      <Box>
        <Text fontSize="xs" color="gray.400" fontStyle="italic" mb={1}>
          Price breakdown:
        </Text>
        <Box as="ul" pl={4} m={0}>
          {includedItems.map((item, index) => {
            const levelText = item.name.replace('Salary Cap:', '').trim();
            return (
              <Text
                key={item.id}
                as="li"
                fontSize="xs"
                color="gray.400"
                fontStyle="italic"
                mb={0.5}
              >
                {levelText || `Item ${index + 1}`}: ${item.price.toFixed(2)}
              </Text>
            );
          })}
        </Box>
        <Text fontSize="xs" color="gray.400" fontStyle="italic" mt={1}>
          Total: ${getCumulativePrice(selectedItemId).toFixed(2)}
        </Text>
      </Box>
    );
  };

  // Other items are already filtered out (non-salary cap items)
  const otherItems = inventoryItems;

  // Early return after all hooks
  if (rosterLoading) {
    return (
      <Center py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text ml={4} color="gray.500">Loading roster...</Text>
      </Center>
    );
  }

  // Get current salary cap from team (already in millions, e.g., 175 = 175M)
  const currentSalaryCap = parseInt((team as any)?.salary_cap.toString()) || 0;

  // Determine current budget tier based on salary cap items
  const getCurrentBudgetTier = (): string | null => {
    if (!salaryCapItems || salaryCapItems.length === 0) return null;
    
    // Get all salary cap items sorted by their new_cap_value
    const allCapItems = [...salaryCapItems].sort((a, b) => {
      const rawA = a.raw as any;
      const rawB = b.raw as any;
      const capA = rawA?.item?.metadata?.new_cap_value ?? rawA?.metadata?.new_cap_value ?? 0;
      const capB = rawB?.item?.metadata?.new_cap_value ?? rawB?.metadata?.new_cap_value ?? 0;
      return capA - capB;
    });

    // Find the tier that matches the current salary cap
    // Look for the item where new_cap_value matches or is closest to current cap
    for (let i = 0; i < allCapItems.length; i++) {
      const item = allCapItems[i];
      const raw = item.raw as any;
      console.log('raw', raw);
      const newCapValue = raw?.item?.metadata?.new_cap_value;
      console.log('newCapValue', newCapValue, currentSalaryCap);
      if (newCapValue !== null && newCapValue == currentSalaryCap) {
        // Extract tier name from item name (e.g., "Increase to $200M" -> "$200M")
        // Or try to get it from metadata if available
        const tierName = raw?.item?.description_en
        return tierName || null;
      }
    }
    return null;
  };

  const currentBudgetTier = getCurrentBudgetTier();

  // Format option text: $200M ($50M Increase, Price $39.99)
  const formatOptionText = (item: StoreItem): string => {
    // currentSalaryCap is already in millions
    const raw = item.raw as any;
    const newCapValue = raw?.item?.metadata?.new_cap_value ?? raw?.metadata?.new_cap_value ?? null;
    const cumulativeIncrease = newCapValue !== null ? newCapValue - currentSalaryCap : getCumulativeIncrease(item.id);
    const cumulativePrice = getCumulativePrice(item.id);
    
    return `${item?.name} ($${cumulativeIncrease}M Increase, Price $${cumulativePrice.toFixed(2)})`;
  };

  const handleAddToCart = (item: StoreItem) => {
    const shopItem = {
      id: item.id,
      name: item.name,
      description: item.description || '',
      price: item.price,
      currency: item.currency || 'USD',
      category: (item.category as any) || 'upgrades',
    };

    // Check if this is a salary cap item and if there's already one in the cart
    if (shopItem.name.includes('Increase to') && hasSalaryCapItem()) {
      setPendingItem(item);
      setShowReplaceDialog(true);
      return;
    }

    // Otherwise, add the item normally
    addItem(shopItem);
  };

  const handleReplaceConfirm = (result: 'Yes' | 'No') => {
    if (result === 'Yes' && pendingItem) {
      // Remove existing salary cap item
      const existingSalaryCapItem = getSalaryCapItem();
      if (existingSalaryCapItem) {
        removeItem(existingSalaryCapItem.item.id);
      }
      // Add the new item
      const shopItem = {
        id: pendingItem.id,
        name: pendingItem.name,
        description: pendingItem.description || '',
        price: pendingItem.price,
        currency: pendingItem.currency || 'USD',
        category: (pendingItem.category as any) || 'upgrades',
      };
      addItem(shopItem);
    }
    setShowReplaceDialog(false);
    setPendingItem(null);
  };


  return (
    <VStack spacing={6} align="stretch">
      {/* Loading / error states */}
      {inventoryLoading && (
        <Center py={8}>
          <Spinner size="lg" color="green.400" />
          <Text ml={3} color="gray.300">Loading shop items...</Text>
        </Center>
      )}
      {!!inventoryError && (
        <Box bg="red.700" color="white" p={3} borderRadius="md">
          There was a problem loading the shop. <Button variant="link" colorScheme="whiteAlpha" onClick={refetch}>Retry</Button>
        </Box>
      )}

      {!inventoryLoading && !inventoryError && (
        <VStack spacing={8} align="stretch">
          {/* Salary Cap Increase Section */}
          {availableSalaryCapItems.length > 0 && (
            <GlassCard p={6} baseColor="#002e0b">
              <HStack justify="space-between" align="center" mb={4}>
                <Heading size="md" color="white">
                  Salary Cap Increase
                </Heading>
                <HStack spacing={4}>
                  <Text color="white" fontSize="sm" fontWeight="medium">
                    CURRENT PAYROLL: ${(totalPayroll / 1000000).toFixed(0)}M
                  </Text>
                  <Text color="white" fontSize="sm" fontWeight="medium">
                    |
                  </Text>
                  <Text color="white" fontSize="sm" fontWeight="medium">
                    SALARY CAP: ${currentSalaryCap}M{currentBudgetTier ? ` (${currentBudgetTier})` : ''}
                  </Text>
                </HStack>
              </HStack>
              <Text color="gray.300" fontSize="sm" mb={4} lineHeight="1.6">
                Maximize your roster opportunities by upgrading your Salary Cap. You can increase your cap by as many levels as you need. In the off-season, you'll be able to retain your Salary Cap Level for just ${renewFee}!
              </Text>
              <Box>
                <Table variant="simple" size="sm" w="100%" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '450px' }} />
                    <col style={{ width: 'auto' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '140px' }} />
                  </colgroup>
                  <Thead>
                    <Tr>
                      <Th color="white" w="400px">ITEM</Th>
                      <Th color="white" textAlign="left">DESCRIPTION</Th>
                      <Th color="white" textAlign="right" w="120px">PRICE</Th>
                      <Th color="white" textAlign="right" w="140px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr style={{ verticalAlign: 'top' }}>
                      <Td color="white" w="400px" style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                        <Select
                          placeholder="Select upgrade..."
                          value={selectedSalaryCapItem}
                          onChange={(e) => setSelectedSalaryCapItem(e.target.value)}
                          bg="whiteAlpha.100"
                          color="white"
                          borderColor="rgba(255, 255, 255, 0.2)"
                          _hover={{ borderColor: 'rgba(255, 255, 255, 0.4)' }}
                          _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                          w="100%"
                        >
                          {availableSalaryCapItems.map((item) => (
                            <option key={item.id} value={item.id} style={{ background: '#1a202c', color: 'white' }}>
                              {formatOptionText(item)}
                            </option>
                          ))}
                        </Select>
                      </Td>
                      <Td color="gray.300" fontSize="sm" textAlign="left" style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                        <VStack align="flex-start" spacing={1}>
                          {selectedSalaryCapItem && (
                            <>
                              <Text textAlign="left">
                                {availableSalaryCapItems.find((i) => i.id === selectedSalaryCapItem)?.description || ''}
                              </Text>
                              {getPriceBreakdown(selectedSalaryCapItem)}
                            </>
                          )}
                        </VStack>
                      </Td>
                      <Td color="green.400" fontWeight="bold" textAlign="right" w="120px" style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                        {selectedSalaryCapItem
                          ? `$${getCumulativePrice(selectedSalaryCapItem).toFixed(2)}`
                          : ''}
                      </Td>
                      <Td textAlign="right" w="140px" style={{ verticalAlign: 'top', paddingTop: '12px' }}>
                        {selectedSalaryCapItem && (
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => {
                              const item = availableSalaryCapItems.find((i) => i.id === selectedSalaryCapItem);
                              if (item) {
                                // Create a combined item with cumulative price
                                const cumulativePrice = getCumulativePrice(selectedSalaryCapItem);
                                const combinedItem: StoreItem = {
                                  ...item,
                                  price: cumulativePrice,
                                  name: `${item.name} (includes all previous levels)`,
                                  category: item.category || 'salary_cap', // Ensure category is set
                                };
                                handleAddToCart(combinedItem);
                              }
                            }}
                          >
                            ADD TO CART
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            </GlassCard>
          )}

          {/* Other Items Sections */}
          {otherItems.length > 0 && (
            <GlassCard p={6} baseColor="#002e0b">
              <Heading size="md" mb={4} color="white">
                {otherItems[0]?.category === 'contract_extension' ? 'Contract Extensions' : 'Other Upgrades'}
              </Heading>
              <Box>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="white">ITEM</Th>
                      <Th color="white">DESCRIPTION</Th>
                      <Th color="white" isNumeric>PRICE</Th>
                      <Th color="white"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {otherItems.map((item) => (
                      <Tr key={item.id}>
                        <Td color="white" fontWeight="medium">
                          {item.name}
                        </Td>
                        <Td color="gray.300" fontSize="sm">
                          {item.description}
                        </Td>
                        <Td color="green.400" fontWeight="bold" isNumeric>
                          ${item.price.toFixed(2)}
                        </Td>
                        <Td>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => handleAddToCart(item)}
                          >
                            ADD TO CART
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </GlassCard>
          )}
        </VStack>
      )}

      {/* Replace Salary Cap Item Dialog */}
      <ConfirmDialog
        isOpen={showReplaceDialog}
        onClose={() => {
          setShowReplaceDialog(false);
          setPendingItem(null);
        }}
        onConfirm={handleReplaceConfirm}
        title="Replace Salary Cap Upgrade?"
        messages={[
          'You already have a salary cap upgrade in your cart.',
          'You can only have one salary cap upgrade at a time.',
          'Would you like to remove the existing item and add this one instead?'
        ]}
        type="warning"
        button1="Replace"
        button2="Cancel"
      />
    </VStack>
  );
};

export default UpgradeShop;

